import {Button, Text, Tile} from "@wakelet/ui-kit";
import {translateKey} from "../language/translate";
import {Spinner} from "../loading/Spinner";
import {PasswordInput} from "../form/PasswordInput";
import {inputClass} from "../form/input-class";
import {FC, useEffect} from "react";
import {FieldErrorsImpl, useForm} from "react-hook-form";
import {FieldGroup, ValidationError} from "../form/ValidationError";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {useChangePassword} from "./useChangePassword";
import {useAuth} from "../auth/AuthContext";
import {getWakeletUrl} from "../../env/env";
import {FieldLabel, RequiredFieldsMessage} from "../form/FieldLabel";

type ChangePasswordFormValues = {
  oldPassword: string;
  newPassword: string;
  serverError: void;
};

const schema = yup.object({
  oldPassword: yup.string().required(translateKey("account:validation.required.oldPassword")),
  newPassword: yup.string().required(translateKey("account:validation.required.newPassword")).min(8, translateKey("account:validation.password")),
}).required();

export const ChangePassword: FC = () => {
  const {user} = useAuth();
  const {changePassword, isSubmitting, isSuccess} = useChangePassword(2000);
  const {
    handleSubmit,
    register,
    formState: {errors},
    setError,
    setValue,
    setFocus
  } = useForm<ChangePasswordFormValues>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const firstError = (
      Object.keys(errors) as Array<keyof typeof errors>
    ).reduce<keyof typeof errors | null>((field, a) => {
      const fieldKey = field as keyof typeof errors;
      return errors?.[fieldKey] ? fieldKey : a;
    }, null);

    if (firstError) {
      if (firstError === "serverError") {
        setFocus("oldPassword");
      } else {
        setFocus(firstError);
      }
    }
  }, [errors, setFocus]);

  const doSubmit = async (values: ChangePasswordFormValues) => {
    if (!changePassword) {
      return;
    }
    return changePassword(values.oldPassword, values.newPassword).then(() => {
      setValue("oldPassword", "");
      setValue("newPassword", "");
    }).catch(err => {
      const {i18nKey} = err;
      setError("serverError", {message: i18nKey.key});
    });
  };

  if (isSuccess) {
    const uri = getWakeletUrl("/accounts/reauthenticate?destination=settings")
    window.location.assign(uri);
  }

  const getAriaError = (errors: FieldErrorsImpl, field: "oldPassword" | "newPassword") => {
    if (errors.serverError?.message) {
      return "change-password-error";
    }

    if (errors.oldPassword && field === "oldPassword") {
      return "old-password-error";
    }

    if (errors.newPassword && field === "newPassword") {
      return "new-password-error";
    }

    return undefined;
  }

  return <Tile>
    <Text.Heading level={3}>{translateKey("account:change.password.title")}</Text.Heading>
    {isSuccess && <h1>Password updated</h1>}
    <form onSubmit={handleSubmit(doSubmit)}
          aria-describedby={errors.serverError?.message && "change-password-error"}
    >
      <ValidationError textCenter={true} message={errors.serverError?.message} id="change-password-error"/>
      <FieldGroup>
        <FieldLabel htmlFor="old-password">{translateKey("account:password.label.old")}</FieldLabel>
        <input type="hidden" name="email" value={user?.email} readOnly/>
        <PasswordInput
          id="old-password"
          type="password"
          {...register("oldPassword")}
          className={inputClass()}
          disabled={isSubmitting}
          data-testid="old-password"
          aria-required={true}
          placeholder={translateKey("account:password.label.old")}
          autoComplete="current-password"
          aria-invalid={!!errors.oldPassword}
          aria-describedby={getAriaError(errors, "oldPassword")}
        />
        <ValidationError message={errors.oldPassword?.message} id="old-password-error"/>
      </FieldGroup>
      <FieldGroup>
        <FieldLabel htmlFor="new-password"><Text>{translateKey("account:password.label.new")}</Text></FieldLabel>
        <PasswordInput
          id="new-password"
          type="password"
          {...register("newPassword")}
          className={inputClass()}
          disabled={isSubmitting}
          data-testid="new-password"
          placeholder={translateKey("account:password.label.new")}
          autoComplete="new-password"
          aria-invalid={!!errors.newPassword}
          aria-describedby={getAriaError(errors, "newPassword")}
        />
        <ValidationError message={errors.newPassword?.message} id="new-password-error"/>
      </FieldGroup>
      <RequiredFieldsMessage/>
      <Button type="submit" disabled={isSubmitting} data-testid="save-btn"
              className="w-32 flex justify-center items-center float-right mt-2">
        {!isSubmitting && <Button.Label text={translateKey("common:buttons.save")}/>}
        {isSubmitting && <Spinner/>}
      </Button>
    </form>
  </Tile>
};
